import React, { useState, useEffect } from 'react';
import { Tabs, Button } from 'antd';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { WmsProgressFilterInput } from '../graphql/queries/getWmsProgress';
import WmsProgressFilter from './components/WmsProgressFilter';
import ByDestinationTab from './components/ByDestinationTab';
import ByPreviousStationTab from './components/ByPreviousStationTab';

const WarehouseStatusPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouseContext();

  // 初始化过滤器
  const [filters, setFilters] = useState<WmsProgressFilterInput>({
    currentWarehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : -1
  });
  const [appliedFilters, setAppliedFilters] = useState<WmsProgressFilterInput>({
    currentWarehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : -1
  });

  // 监听currentWarehouse变化，确保currentWarehouseId始终有效
  useEffect(() => {
    if (currentWarehouse?.id) {
      const warehouseId = Number(currentWarehouse.id);
      setFilters(prevFilters => {
        if (warehouseId !== prevFilters.currentWarehouseId) {
          const updatedFilters = {
            ...prevFilters,
            currentWarehouseId: warehouseId
          };
          setAppliedFilters(updatedFilters);
          return updatedFilters;
        }
        return prevFilters;
      });
    }
  }, [currentWarehouse?.id]);

  const handleFilter = () => {
    setAppliedFilters({ ...filters });
  };

  const handleResetFilters = () => {
    const resetFilters: WmsProgressFilterInput = {
      currentWarehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  // 处理重置（包含默认时间范围）
  const handleReset = () => {
    // 设置默认时间范围（一周）
    const end = dayjs().minute(0).second(0).millisecond(0);
    const start = end.subtract(7, 'day').minute(0).second(0).millisecond(0);
    
    const resetFilters: WmsProgressFilterInput = {
      currentWarehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : 0,
      arrivedCurrentWarehouseDateRange: {
        start: start.toISOString(),
        end: end.toISOString()
      },
      channelId: undefined,
      carrierCode: undefined
    };
    
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  const items: TabsProps['items'] = [
    {
      key: 'destination',
      label: t('wms.progress.tabs.byDestination'),
      children: <ByDestinationTab appliedFilters={appliedFilters} />
    }
    ,{
      key: 'previous-station',
      label: t('wms.progress.tabs.byPreviousStation'),
      children: <ByPreviousStationTab appliedFilters={appliedFilters} />
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      {/* 筛选条件 */}
      <WmsProgressFilter
        filters={filters}
        setFilters={setFilters}
        handleFilter={handleFilter}
        handleResetFilters={handleResetFilters}
      />

      {/* 批量操作区域 */}
      <div className="flex justify-end items-center gap-2 mb-3 pl-0">
        <Button 
          type="primary" 
          onClick={handleFilter}
          className="w-auto min-w-24"
        >
          <span className="font-bold">{t('wms.exceptions.filters.filter')}</span>
        </Button>
        <Button 
          onClick={handleReset}
          className="w-auto min-w-24"
        >
          <span className="font-bold">{t('wms.exceptions.filters.reset')}</span>
        </Button>
      </div>

      {/* Tab页签 */}
      <Tabs defaultActiveKey="destination" items={items} className="[&_.ant-tabs-tab]:font-bold" />
    </div>
  );
};

export default WarehouseStatusPage;
